import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Grid,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import api from '../../lib/api';
import AdminPageShell from '../../components/admin/AdminPageShell';
import Link from '../../components/Link';
import { ADMIN_COMPACT_BUTTON } from '../../styles/adminStyles';
import { hoursFromMinutes, reportField, reportNumber } from '../../lib/reportFields';
import { downloadCsv, reportCsvFilename, toCsv } from '../../lib/reportCsv';
import {
  buildReportsPath,
  parseReportsQuery,
  periodPreset,
  REPORT_NETWORK_CENTER_ID,
  REPORT_NETWORK_CENTER_LABEL,
  REPORT_PERIOD_PRESETS,
  REPORT_TAB_KEYS,
  REPORT_TAB_LABELS,
} from '../../lib/reportPeriod';
import { abonementExpiryBadge, ABONEMENT_EXPIRY_COLORS } from '../../lib/abonementVisitLabels';
import { fetchCenters } from '../../ducks/centers';

function centerIdOf(center) {
  if (!center) return '';
  const id = center.id != null ? center.id : center.Id;
  return id != null && id !== '' ? String(id) : '';
}

function centerNameOf(center) {
  if (!center) return 'Без названия';
  return center.name || center.Name || 'Без названия';
}

function isNetworkCenter(centerId) {
  return String(centerId) === REPORT_NETWORK_CENTER_ID;
}

function formatReportDate(value) {
  if (value == null || value === '') return '-';
  const m = moment(value);
  if (!m.isValid() || m.year() < 2000) return '-';
  return m.format('DD.MM.YYYY');
}

function clientRowKey(row, index) {
  const source = reportField(row, 'Source', 'source', '');
  const businessId = reportNumber(row, 'BusinessClientId', 'businessClientId');
  const userId = reportNumber(row, 'UserId', 'userId');
  const id = reportNumber(row, 'Id', 'id');
  return `${source}-${businessId || id}-${userId || 0}-${index}`;
}

const styles = theme => ({
  pageBody: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
  },
  table: {
    minWidth: 650,
    '& thead th': {
      backgroundColor: '#f8f9fa',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#495057',
      borderBottom: '2px solid #dee2e6',
    },
    '& tbody td': {
      borderBottom: '1px solid #e9ecef',
      padding: theme.spacing(1.5),
    },
  },
  filterSection: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    minHeight: 200,
  },
  filterTextField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: 8,
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    },
  },
  centerSelect: {
    minWidth: 280,
    backgroundColor: '#ffffff',
    borderRadius: 8,
  },
  applyButton: {
    ...ADMIN_COMPACT_BUTTON,
  },
  tabs: {
    minHeight: 40,
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.8125rem',
      minHeight: 40,
      minWidth: 96,
      padding: '6px 12px',
      '&.Mui-selected': {
        fontWeight: 600,
      },
    },
    '& .MuiTabs-indicator': {
      height: 2,
      borderRadius: '2px 2px 0 0',
    },
  },
  periodLabel: {
    fontWeight: 600,
    color: '#495057',
    marginRight: theme.spacing(1),
    fontSize: '0.8125rem',
  },
  comparisonCard: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    height: '100%',
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
  },
  hint: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  presetButton: {
    ...ADMIN_COMPACT_BUTTON,
  },
  csvButton: {
    ...ADMIN_COMPACT_BUTTON,
  },
  inlineLink: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

class Reports extends Component {
  constructor(props) {
    super(props);
    const parsed = parseReportsQuery(props.location && props.location.search);
    this.state = {
      activeTab: parsed.tab,
      loading: false,
      errorMessage: '',
      fromDate: parsed.from || moment().subtract(1, 'month').format('YYYY-MM-DD'),
      toDate: parsed.to || moment().format('YYYY-MM-DD'),
      periodType: 'day',
      attendedOnly: parsed.attendedOnly,
      summary: null,
      bookingsStatistics: [],
      popularTimes: null,
      playgroundsStatistics: [],
      revenueByRentalItems: [],
      periodComparison: null,
      trainers: null,
      receivables: null,
      clients: null,
      organizationId: null,
      playgroundsPage: 0,
      playgroundsRowsPerPage: 20,
      bookingsPage: 0,
      bookingsRowsPerPage: 20,
      period1From: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
      period1To: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
      period2From: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
      period2To: moment().endOf('month').format('YYYY-MM-DD'),
    };
  }

  componentDidMount() {
    this.loadCenters();
    this.ensureCenterInUrl();
    this.loadCurrentReport();
  }

  componentDidUpdate(prevProps) {
    const hadCentersLen = (prevProps.centers || []).length;
    const hasCentersLen = (this.props.centers || []).length;
    if (hasCentersLen > 0 && hadCentersLen === 0) {
      this.ensureCenterInUrl();
      if (this.getCenterId()) {
        this.loadCurrentReport();
      }
    }
    if (!this.getCenterId() && hasCentersLen > 0) {
      this.ensureCenterInUrl();
      return;
    }
    const prevSearch = prevProps.location ? prevProps.location.search : '';
    const nextSearch = this.props.location ? this.props.location.search : '';
    if (prevSearch === nextSearch) return;
    const parsed = parseReportsQuery(nextSearch);
    this.setState({
      fromDate: parsed.from || this.state.fromDate,
      toDate: parsed.to || this.state.toDate,
      activeTab: parsed.tab,
      attendedOnly: parsed.attendedOnly,
    }, () => {
      this.loadCurrentReport();
    });
  }

  reportsPath = (overrides = {}) => {
    const { fromDate, toDate, activeTab, attendedOnly } = this.state;
    return buildReportsPath({
      centerId: overrides.centerId != null ? overrides.centerId : this.getCenterId(),
      from: overrides.fromDate != null ? overrides.fromDate : fromDate,
      to: overrides.toDate != null ? overrides.toDate : toDate,
      tab: overrides.activeTab != null ? overrides.activeTab : activeTab,
      attendedOnly: overrides.attendedOnly != null ? overrides.attendedOnly : attendedOnly,
    });
  };

  replaceReportsUrl = (overrides = {}) => {
    if (!this.props.history || !this.props.location) return false;
    const path = this.reportsPath(overrides);
    const current = `${this.props.location.pathname}${this.props.location.search || ''}`;
    if (current === path) return false;
    this.props.history.push(path);
    return true;
  };

  loadCenters = async () => {
    if (!this.props.fetchCenters) return;
    if ((this.props.centers || []).length > 0) return;
    try {
      await this.props.fetchCenters();
    } catch (e) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await this.props.fetchCenters();
      } catch (e2) {
        this.setState({ errorMessage: e2.message || 'Не удалось загрузить список клубов' });
      }
    }
  };

  getCenterIdFromProps = (props) => {
    const search = props && props.location ? props.location.search : '';
    const query = new URLSearchParams(search || '');
    return query.get('centerId') || '';
  };

  getCenterId = () => this.getCenterIdFromProps(this.props);

  ensureCenterInUrl = () => {
    if (this.getCenterId()) return;
    const centers = this.props.centers || [];
    if (!centers.length || !this.props.history) return;
    const firstId = centerIdOf(centers[0]);
    if (!firstId) return;
    this.props.history.replace(this.reportsPath({ centerId: firstId }));
  };

  handleCenterSelectChange = (event) => {
    const nextCenterId = event.target.value;
    if (!nextCenterId || !this.props.history) return;
    this.props.history.push(this.reportsPath({ centerId: nextCenterId }));
  };

  resolveReportScope = async () => {
    const centerId = this.getCenterId();
    if (!centerId) return null;
    if (isNetworkCenter(centerId)) {
      const firstId = centerIdOf((this.props.centers || [])[0]);
      if (!firstId) return null;
      let organizationId = this.state.organizationId;
      if (!organizationId) {
        const org = await api.getOrganizationByComplex(firstId);
        organizationId = org && (org.id != null ? org.id : org.Id);
        if (organizationId) {
          this.setState({ organizationId });
        }
      }
      if (!organizationId) {
        throw new Error('Не удалось определить организацию сети');
      }
      return { organizationId };
    }
    return { complexId: centerId };
  };

  loadCurrentReport = async () => {
    const centerId = this.getCenterId();
    if (!centerId) {
      this.setState({ loading: false, errorMessage: '' });
      return;
    }

    const { activeTab, fromDate, toDate, periodType, attendedOnly } = this.state;
    this.setState({ loading: true, errorMessage: '' });

    try {
      const scope = await this.resolveReportScope();
      if (!scope) {
        this.setState({ loading: false });
        return;
      }
      switch (activeTab) {
        case 0: {
          const summary = await api.getReportsSummary(scope, fromDate, toDate);
          this.setState({ summary: summary || null });
          break;
        }
        case 1: {
          const [stats, rental] = await Promise.all([
            api.getBookingsStatistics(fromDate, toDate, periodType, scope),
            api.getRevenueByRentalItems(fromDate, toDate, scope),
          ]);
          this.setState({
            bookingsStatistics: stats || [],
            revenueByRentalItems: rental || [],
            bookingsPage: 0,
          });
          break;
        }
        case 2: {
          const playgrounds = await api.getPlaygroundsStatistics(fromDate, toDate, scope);
          this.setState({ playgroundsStatistics: playgrounds || [], playgroundsPage: 0 });
          break;
        }
        case 3: {
          const trainers = await api.getReportsTrainers(scope, fromDate, toDate, attendedOnly);
          this.setState({ trainers: trainers || null });
          break;
        }
        case 4: {
          const receivables = await api.getReportsReceivables(scope, fromDate, toDate);
          this.setState({ receivables: receivables || null });
          break;
        }
        case 5: {
          const popular = await api.getPopularTimes(fromDate, toDate, scope);
          this.setState({ popularTimes: popular });
          break;
        }
        case 6: {
          const { period1From, period1To, period2From, period2To } = this.state;
          const comparison = await api.getPeriodComparison(
            period1From,
            period1To,
            period2From,
            period2To,
            scope
          );
          this.setState({ periodComparison: comparison || null });
          break;
        }
        case 7: {
          const clients = await api.getReportsClients(scope, fromDate, toDate);
          this.setState({ clients: clients || null });
          break;
        }
        default:
          break;
      }
    } catch (error) {
      const next = {
        errorMessage: error.message || 'Не удалось загрузить отчёт',
      };
      if (activeTab === 0) next.summary = null;
      if (activeTab === 1) {
        next.bookingsStatistics = [];
        next.revenueByRentalItems = [];
      }
      if (activeTab === 2) next.playgroundsStatistics = [];
      if (activeTab === 3) next.trainers = null;
      if (activeTab === 4) next.receivables = null;
      if (activeTab === 5) next.popularTimes = null;
      if (activeTab === 6) next.periodComparison = null;
      if (activeTab === 7) next.clients = null;
      this.setState(next);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleTabChange = (event, newValue) => {
    if (!this.replaceReportsUrl({ activeTab: newValue })) {
      this.setState({ activeTab: newValue }, () => {
        this.loadCurrentReport();
      });
    }
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  handleApplyFilters = () => {
    if (!this.replaceReportsUrl()) {
      this.loadCurrentReport();
    }
  };

  handlePeriodPreset = (key) => {
    const preset = periodPreset(key);
    if (!preset) return;
    this.setState({ fromDate: preset.from, toDate: preset.to }, () => {
      if (!this.replaceReportsUrl()) {
        this.loadCurrentReport();
      }
    });
  };

  handleAttendedOnly = (event) => {
    const attendedOnly = !!(event.target && event.target.checked);
    this.setState({ attendedOnly }, () => {
      if (!this.replaceReportsUrl()) {
        this.loadCurrentReport();
      }
    });
  };

  sectionsHref = (view) => {
    const centerId = this.getCenterId();
    const fallbackId = isNetworkCenter(centerId)
      ? centerIdOf((this.props.centers || [])[0])
      : centerId;
    if (!fallbackId) return `/sections/${view}`;
    return `/sections/${view}?centerId=${encodeURIComponent(String(fallbackId))}`;
  };

  handleExportCsv = () => {
    const { activeTab, fromDate, toDate } = this.state;
    const payload = this.buildCsvPayload(activeTab);
    if (!payload) return;
    downloadCsv(reportCsvFilename(REPORT_TAB_LABELS[activeTab] || REPORT_TAB_KEYS[activeTab], fromDate, toDate), payload);
  };

  buildCsvPayload = (tab) => {
    if (tab === 0) {
      const { summary } = this.state;
      if (!summary) return null;
      const lifecycle = reportField(summary, 'Lifecycle', 'lifecycle', {}) || {};
      const sources = [
        { name: 'От аренды площадки', ...this.moneySource(reportField(summary, 'PlaygroundRent', 'playgroundRent', {})) },
        { name: 'От доп. услуг и проката', ...this.moneySource(reportField(summary, 'Rental', 'rental', {})) },
        { name: 'Абонементы', ...this.moneySource(reportField(summary, 'Abonements', 'abonements', {})) },
      ];
      return toCsv(
        ['Показатель', 'Значение'],
        [
          ['Начислено', reportNumber(summary, 'Accrued', 'accrued')],
          ['Оплачено', reportNumber(summary, 'Paid', 'paid')],
          ['К получению', reportNumber(summary, 'Receivable', 'receivable')],
          ['Загрузка зала', reportNumber(summary, 'OccupancyHallPercent', 'occupancyHallPercent')],
          ['Часы тренеров', reportNumber(summary, 'TrainerHours', 'trainerHours')],
          ['Заявки', reportNumber(lifecycle, 'RequestCount', 'requestCount')],
          ['Одобрено', reportNumber(lifecycle, 'ApprovedCount', 'approvedCount')],
          ['Оформлен абонемент', reportNumber(lifecycle, 'ConvertedToAbonementCount', 'convertedToAbonementCount')],
          ['Истекает', reportNumber(lifecycle, 'ExpiringAbonementCount', 'expiringAbonementCount')],
          ['Просрочен', reportNumber(lifecycle, 'OverdueAbonementCount', 'overdueAbonementCount')],
          ['Клиенты организации', reportNumber(lifecycle, 'ClientCount', 'clientCount')],
        ].concat(sources.map((row) => [row.name, row.accrued]))
      );
    }
    if (tab === 1) {
      const rows = (this.state.bookingsStatistics || []).map((row) => ([
        reportField(row, 'Period', 'period', '-'),
        reportNumber(row, 'TotalRevenue', 'totalRevenue'),
        reportNumber(row, 'PlaygroundRevenue', 'playgroundRevenue'),
        reportNumber(row, 'RentalItemsRevenue', 'rentalItemsRevenue'),
        reportNumber(row, 'PaidRevenue', 'paidRevenue'),
        reportNumber(row, 'TotalCount', 'totalCount'),
        reportNumber(row, 'OpenCount', 'openCount'),
        reportNumber(row, 'PaidCount', 'paidCount'),
        reportNumber(row, 'CanceledCount', 'canceledCount'),
      ]));
      return toCsv(
        ['Период', 'Начислено', 'От аренды площадки', 'От доп услуг', 'Оплачено', 'Всего бронирований', 'Открыто', 'Оплачено шт', 'Отменено'],
        rows
      );
    }
    if (tab === 2) {
      const rows = (this.state.playgroundsStatistics || []).map((row) => ([
        reportField(row, 'PlaygroundName', 'playgroundName', '-'),
        reportNumber(row, 'OccupancyRentPercent', 'occupancyRentPercent'),
        reportNumber(row, 'OccupancySectionPercent', 'occupancySectionPercent'),
        reportNumber(row, 'OccupancyHallPercent', 'occupancyHallPercent', reportNumber(row, 'OccupancyRate', 'occupancyRate')),
        reportNumber(row, 'TotalRevenue', 'totalRevenue'),
        reportNumber(row, 'TotalCount', 'totalCount'),
      ]));
      return toCsv(['Площадка', 'Аренда %', 'Секции %', 'Зал %', 'Начислено', 'Количество'], rows);
    }
    if (tab === 3) {
      const items = reportField(this.state.trainers, 'Items', 'items', []) || [];
      const rows = items.map((row) => ([
        reportField(row, 'TrainerName', 'trainerName', 'Без тренера'),
        reportNumber(row, 'GroupCount', 'groupCount'),
        hoursFromMinutes(reportNumber(row, 'GroupMinutes', 'groupMinutes')),
        reportNumber(row, 'PersonalCount', 'personalCount'),
        hoursFromMinutes(reportNumber(row, 'PersonalMinutes', 'personalMinutes')),
        hoursFromMinutes(reportNumber(row, 'TotalMinutes', 'totalMinutes')),
        reportNumber(row, 'BookedCount', 'bookedCount'),
        reportNumber(row, 'AttendedCount', 'attendedCount'),
        reportNumber(row, 'NoShowCount', 'noShowCount'),
      ]));
      return toCsv(
        ['Тренер', 'Группы шт', 'Часы групп', 'Персоналки шт', 'Часы персоналок', 'Всего часов', 'Записано', 'Явка', 'Неявка'],
        rows
      );
    }
    if (tab === 4) {
      const receivables = this.state.receivables;
      const bookings = reportField(receivables, 'Bookings', 'bookings', []) || [];
      const abonements = reportField(receivables, 'Abonements', 'abonements', []) || [];
      const expiring = reportField(receivables, 'ExpiringAbonements', 'expiringAbonements', []) || [];
      const overdue = reportField(receivables, 'OverdueAbonements', 'overdueAbonements', []) || [];
      const rows = []
        .concat(bookings.map((row) => ([
          'Бронь',
          reportField(row, 'PlaygroundName', 'playgroundName', '-'),
          moment(reportField(row, 'Date', 'date')).format('DD.MM.YYYY'),
          reportNumber(row, 'Price', 'price'),
          '',
        ])))
        .concat(abonements.map((row) => ([
          'Неоплаченный абонемент',
          reportField(row, 'UserName', 'userName', '-'),
          `${moment(reportField(row, 'DateFrom', 'dateFrom')).format('DD.MM.YYYY')} — ${moment(reportField(row, 'DateTo', 'dateTo')).format('DD.MM.YYYY')}`,
          reportNumber(row, 'Amount', 'amount'),
          '',
        ])))
        .concat(expiring.map((row) => ([
          'Истекает',
          reportField(row, 'UserName', 'userName', '-'),
          `${moment(reportField(row, 'DateFrom', 'dateFrom')).format('DD.MM.YYYY')} — ${moment(reportField(row, 'DateTo', 'dateTo')).format('DD.MM.YYYY')}`,
          reportNumber(row, 'Amount', 'amount'),
          abonementExpiryBadge(row).label,
        ])))
        .concat(overdue.map((row) => ([
          'Просрочен',
          reportField(row, 'UserName', 'userName', '-'),
          `${moment(reportField(row, 'DateFrom', 'dateFrom')).format('DD.MM.YYYY')} — ${moment(reportField(row, 'DateTo', 'dateTo')).format('DD.MM.YYYY')}`,
          reportNumber(row, 'Amount', 'amount'),
          abonementExpiryBadge(row).label,
        ])));
      return toCsv(['Тип', 'Клиент / площадка', 'Период', 'Сумма', 'Срок'], rows);
    }
    if (tab === 5) {
      const popularTimes = this.state.popularTimes;
      const days = (reportField(popularTimes, 'PopularDays', 'popularDays', []) || []).map((row) => ([
        'День',
        reportField(row, 'DayOfWeek', 'dayOfWeek', '-'),
        reportNumber(row, 'Count', 'count'),
        reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      ]));
      const hours = (reportField(popularTimes, 'PopularHours', 'popularHours', []) || []).map((row) => ([
        'Час',
        reportNumber(row, 'Hour', 'hour'),
        reportNumber(row, 'Count', 'count'),
        reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      ]));
      return toCsv(['Тип', 'Значение', 'Количество', 'Начислено'], days.concat(hours));
    }
    if (tab === 6) {
      const { periodComparison, period1From, period1To, period2From, period2To } = this.state;
      const Period1 = reportField(periodComparison, 'Period1', 'period1', {}) || {};
      const Period2 = reportField(periodComparison, 'Period2', 'period2', {}) || {};
      const Changes = reportField(periodComparison, 'Changes', 'changes', {}) || {};
      return toCsv(
        ['Период', 'От', 'До', 'Выручка', 'Количество'],
        [
          ['Период 1', period1From, period1To, reportNumber(Period1, 'Revenue', 'revenue'), reportNumber(Period1, 'Count', 'count')],
          ['Период 2', period2From, period2To, reportNumber(Period2, 'Revenue', 'revenue'), reportNumber(Period2, 'Count', 'count')],
          ['Изменение %', '', '', reportNumber(Changes, 'RevenueChange', 'revenueChange'), reportNumber(Changes, 'CountChange', 'countChange')],
        ]
      );
    }
    if (tab === 7) {
      const clients = this.state.clients;
      const items = reportField(clients, 'Items', 'items', []) || [];
      const rows = items.map((row) => ([
        reportField(row, 'DisplayName', 'displayName', '-'),
        reportField(row, 'Phone', 'phone', ''),
        reportField(row, 'Source', 'source', ''),
        formatReportDate(reportField(row, 'CreatedAt', 'createdAt')),
      ]));
      return toCsv(['Клиент', 'Телефон', 'Источник', 'Создан'], rows);
    }
    return null;
  };

  formatCurrency = (value) => {
    const numValue = typeof value === 'number' && !isNaN(value) ? value : (parseFloat(value) || 0);
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(numValue);
  };

  formatHours = (value) => {
    const n = typeof value === 'number' ? value : parseFloat(value);
    const hours = Number.isFinite(n) ? n : 0;
    return `${hours.toFixed(1)} ч`;
  };

  formatPercent = (value) => {
    const n = typeof value === 'number' ? value : parseFloat(value);
    return `${Number.isFinite(n) ? n : 0}%`;
  };

  moneySource = (row) => ({
    accrued: reportNumber(row, 'Accrued', 'accrued'),
    paid: reportNumber(row, 'Paid', 'paid'),
    receivable: reportNumber(row, 'Receivable', 'receivable'),
  });

  handleChangePage = (event, newPage) => {
    this.setState({ playgroundsPage: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ playgroundsRowsPerPage: parseInt(event.target.value, 10), playgroundsPage: 0 });
  };

  handleChangeBookingsPage = (event, newPage) => {
    this.setState({ bookingsPage: newPage });
  };

  handleChangeBookingsRowsPerPage = (event) => {
    this.setState({ bookingsRowsPerPage: parseInt(event.target.value, 10), bookingsPage: 0 });
  };

  renderSummary = () => {
    const { summary } = this.state;
    const { classes } = this.props;
    if (!summary) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const kpis = [
      { label: 'Начислено', value: this.formatCurrency(reportNumber(summary, 'Accrued', 'accrued')) },
      { label: 'Оплачено', value: this.formatCurrency(reportNumber(summary, 'Paid', 'paid')) },
      { label: 'К получению', value: this.formatCurrency(reportNumber(summary, 'Receivable', 'receivable')) },
      { label: 'Загрузка зала', value: this.formatPercent(reportNumber(summary, 'OccupancyHallPercent', 'occupancyHallPercent')) },
      { label: 'Часы тренеров', value: this.formatHours(reportNumber(summary, 'TrainerHours', 'trainerHours')) },
    ];

    const sources = [
      { name: 'От аренды площадки', ...this.moneySource(reportField(summary, 'PlaygroundRent', 'playgroundRent', {})) },
      { name: 'От доп. услуг и проката', ...this.moneySource(reportField(summary, 'Rental', 'rental', {})) },
      { name: 'Абонементы', ...this.moneySource(reportField(summary, 'Abonements', 'abonements', {})) },
    ];

    const lifecycle = reportField(summary, 'Lifecycle', 'lifecycle', {}) || {};
    const funnel = [
      { label: 'Заявки', value: reportNumber(lifecycle, 'RequestCount', 'requestCount') },
      { label: 'Одобрено', value: reportNumber(lifecycle, 'ApprovedCount', 'approvedCount') },
      { label: 'Оформлен абонемент', value: reportNumber(lifecycle, 'ConvertedToAbonementCount', 'convertedToAbonementCount') },
      { label: 'Истекает', value: reportNumber(lifecycle, 'ExpiringAbonementCount', 'expiringAbonementCount') },
      { label: 'Просрочен', value: reportNumber(lifecycle, 'OverdueAbonementCount', 'overdueAbonementCount') },
      { label: 'Клиенты организации', value: reportNumber(lifecycle, 'ClientCount', 'clientCount') },
    ];
    const isNetwork = !!reportField(summary, 'IsNetwork', 'isNetwork', false);
    const complexCount = reportNumber(summary, 'ComplexCount', 'complexCount');

    return (
      <>
        <Grid container spacing={2}>
          {kpis.map((kpi) => (
            <Grid item xs={12} sm={6} md={4} key={kpi.label}>
              <Paper className={classes.comparisonCard}>
                <Typography variant="body2" color="textSecondary">{kpi.label}</Typography>
                <Typography variant="h6" style={{ fontWeight: 600, marginTop: 8 }}>{kpi.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {isNetwork ? (
          <Typography variant="body2" className={classes.hint} style={{ marginTop: 8 }}>
            {REPORT_NETWORK_CENTER_LABEL}: {complexCount}
          </Typography>
        ) : null}
        <Typography variant="h6" className={classes.sectionTitle}>Заявки и абонементы</Typography>
        <Grid container spacing={2}>
          {funnel.map((kpi) => (
            <Grid item xs={12} sm={6} md={4} key={kpi.label}>
              <Paper className={classes.comparisonCard}>
                <Typography variant="body2" color="textSecondary">{kpi.label}</Typography>
                <Typography variant="h6" style={{ fontWeight: 600, marginTop: 8 }}>{kpi.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" className={classes.hint} style={{ marginTop: 8 }}>
          <Link className={classes.inlineLink} to={this.sectionsHref('abonements')}>Абонементы</Link>
          {' · '}
          <Link className={classes.inlineLink} to={this.sectionsHref('requests')}>Заявки</Link>
          {' · '}
          <Link className={classes.inlineLink} to={this.sectionsHref('clients')}>Клиенты</Link>
        </Typography>
        <Typography variant="h6" className={classes.sectionTitle}>Источники денег</Typography>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Источник</TableCell>
              <TableCell align="right">Начислено</TableCell>
              <TableCell align="right">Оплачено</TableCell>
              <TableCell align="right">К получению</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map((row) => (
              <TableRow key={row.name}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{this.formatCurrency(row.accrued)}</TableCell>
                <TableCell align="right">{this.formatCurrency(row.paid)}</TableCell>
                <TableCell align="right">{this.formatCurrency(row.receivable)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    );
  };

  renderBookingsStatistics = () => {
    const { bookingsStatistics, bookingsPage, bookingsRowsPerPage } = this.state;
    const { classes } = this.props;

    if (!bookingsStatistics || bookingsStatistics.length === 0) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const processedData = bookingsStatistics.map(row => ({
      period: reportField(row, 'Period', 'period', '-'),
      totalRevenue: reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      playgroundRevenue: reportNumber(row, 'PlaygroundRevenue', 'playgroundRevenue'),
      rentalItemsRevenue: reportNumber(row, 'RentalItemsRevenue', 'rentalItemsRevenue'),
      paidRevenue: reportNumber(row, 'PaidRevenue', 'paidRevenue'),
      totalCount: reportNumber(row, 'TotalCount', 'totalCount'),
      openCount: reportNumber(row, 'OpenCount', 'openCount'),
      paidCount: reportNumber(row, 'PaidCount', 'paidCount'),
      canceledCount: reportNumber(row, 'CanceledCount', 'canceledCount'),
    }));

    const startIndex = bookingsPage * bookingsRowsPerPage;
    const paginatedData = processedData.slice(startIndex, startIndex + bookingsRowsPerPage);

    return (
      <>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Период</TableCell>
              <TableCell align="right">Начислено</TableCell>
              <TableCell align="right">От аренды площадки</TableCell>
              <TableCell align="right">От доп услуг</TableCell>
              <TableCell align="right">Оплачено</TableCell>
              <TableCell align="right">Всего бронирований</TableCell>
              <TableCell align="right">Открыто</TableCell>
              <TableCell align="right">Оплачено</TableCell>
              <TableCell align="right">Отменено</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={startIndex + index}>
                <TableCell style={{ fontWeight: 500 }}>{row.period}</TableCell>
                <TableCell align="right" style={{ fontWeight: 600 }}>{this.formatCurrency(row.totalRevenue)}</TableCell>
                <TableCell align="right">{this.formatCurrency(row.playgroundRevenue)}</TableCell>
                <TableCell align="right">{this.formatCurrency(row.rentalItemsRevenue)}</TableCell>
                <TableCell align="right" style={{ fontWeight: 500, color: '#2e7d32' }}>{this.formatCurrency(row.paidRevenue)}</TableCell>
                <TableCell align="right">{row.totalCount}</TableCell>
                <TableCell align="right">{row.openCount}</TableCell>
                <TableCell align="right" style={{ color: '#2e7d32' }}>{row.paidCount}</TableCell>
                <TableCell align="right" style={{ color: '#d32f2f' }}>{row.canceledCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={processedData.length}
          page={bookingsPage}
          onChangePage={this.handleChangeBookingsPage}
          rowsPerPage={bookingsRowsPerPage}
          onChangeRowsPerPage={this.handleChangeBookingsRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
          style={{ borderTop: '1px solid #e9ecef', marginTop: 8 }}
        />
      </>
    );
  };

  renderRevenueByRentalItems = () => {
    const { revenueByRentalItems } = this.state;
    const { classes } = this.props;

    if (!revenueByRentalItems || revenueByRentalItems.length === 0) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const processedData = revenueByRentalItems.map((row) => ({
      rentalItemName: reportField(row, 'RentalItemName', 'rentalItemName', '-'),
      revenue: reportNumber(row, 'Revenue', 'revenue'),
      count: reportNumber(row, 'Count', 'count'),
      totalQuantity: reportNumber(row, 'TotalQuantity', 'totalQuantity'),
    }));

    return (
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Услуга/Инвентарь</TableCell>
            <TableCell align="right">Выручка</TableCell>
            <TableCell align="right">Количество бронирований</TableCell>
            <TableCell align="right">Общее количество</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.rentalItemName}</TableCell>
              <TableCell align="right">{this.formatCurrency(row.revenue)}</TableCell>
              <TableCell align="right">{row.count}</TableCell>
              <TableCell align="right">{row.totalQuantity}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  renderMoney = () => {
    const { classes } = this.props;
    return (
      <>
        {this.renderBookingsStatistics()}
        <Typography variant="h6" className={classes.sectionTitle}>Выручка от услуг</Typography>
        {this.renderRevenueByRentalItems()}
      </>
    );
  };

  renderPlaygroundsStatistics = () => {
    const { playgroundsStatistics, playgroundsPage, playgroundsRowsPerPage } = this.state;
    const { classes } = this.props;

    const processedData = (playgroundsStatistics || []).map(row => ({
      playgroundName: reportField(row, 'PlaygroundName', 'playgroundName', '-'),
      occupancyRent: reportNumber(row, 'OccupancyRentPercent', 'occupancyRentPercent'),
      occupancySection: reportNumber(row, 'OccupancySectionPercent', 'occupancySectionPercent'),
      occupancyHall: reportNumber(
        row,
        'OccupancyHallPercent',
        'occupancyHallPercent',
        reportNumber(row, 'OccupancyRate', 'occupancyRate')
      ),
      totalRevenue: reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      totalCount: reportNumber(row, 'TotalCount', 'totalCount'),
    }));

    if (processedData.length === 0) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const startIndex = playgroundsPage * playgroundsRowsPerPage;
    const paginatedData = processedData.slice(startIndex, startIndex + playgroundsRowsPerPage);

    return (
      <>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Площадка</TableCell>
              <TableCell align="right">Аренда (%)</TableCell>
              <TableCell align="right">Секции (%)</TableCell>
              <TableCell align="right">Зал (%)</TableCell>
              <TableCell align="right">Начислено</TableCell>
              <TableCell align="right">Количество</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={startIndex + index}>
                <TableCell>{row.playgroundName}</TableCell>
                <TableCell align="right">{row.occupancyRent}%</TableCell>
                <TableCell align="right">{row.occupancySection}%</TableCell>
                <TableCell align="right">{row.occupancyHall}%</TableCell>
                <TableCell align="right">{this.formatCurrency(row.totalRevenue)}</TableCell>
                <TableCell align="right">{row.totalCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={processedData.length}
          page={playgroundsPage}
          onChangePage={this.handleChangePage}
          rowsPerPage={playgroundsRowsPerPage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </>
    );
  };

  renderTrainers = () => {
    const { trainers, attendedOnly } = this.state;
    const { classes } = this.props;
    const items = reportField(trainers, 'Items', 'items', []) || [];

    return (
      <>
        <FormControlLabel
          control={<Switch color="primary" checked={!!attendedOnly} onChange={this.handleAttendedOnly} />}
          label="Только проведённые (явка)"
        />
        <Typography variant="body2" color="textSecondary" className={classes.hint}>
          {attendedOnly
            ? 'Часы групп — только занятия с отметкой явки. Неявка в часы не входит.'
            : 'Чтобы посчитать оплату, умножьте часы (или занятия) на ставку. Ставку система не хранит.'}
        </Typography>
        {!items.length ? (
          <Typography>Нет данных для отображения</Typography>
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Тренер</TableCell>
                <TableCell align="right">Группы, шт</TableCell>
                <TableCell align="right">Часы групп</TableCell>
                <TableCell align="right">Персоналки, шт</TableCell>
                <TableCell align="right">Часы персоналок</TableCell>
                <TableCell align="right">Всего часов</TableCell>
                <TableCell align="right">Записано</TableCell>
                <TableCell align="right">Явка</TableCell>
                <TableCell align="right">Неявка</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row, index) => (
                <TableRow key={reportField(row, 'TrainerId', 'trainerId', index)}>
                  <TableCell>{reportField(row, 'TrainerName', 'trainerName', 'Без тренера')}</TableCell>
                  <TableCell align="right">{reportNumber(row, 'GroupCount', 'groupCount')}</TableCell>
                  <TableCell align="right">{this.formatHours(hoursFromMinutes(reportNumber(row, 'GroupMinutes', 'groupMinutes')))}</TableCell>
                  <TableCell align="right">{reportNumber(row, 'PersonalCount', 'personalCount')}</TableCell>
                  <TableCell align="right">{this.formatHours(hoursFromMinutes(reportNumber(row, 'PersonalMinutes', 'personalMinutes')))}</TableCell>
                  <TableCell align="right">{this.formatHours(hoursFromMinutes(reportNumber(row, 'TotalMinutes', 'totalMinutes')))}</TableCell>
                  <TableCell align="right">{reportNumber(row, 'BookedCount', 'bookedCount')}</TableCell>
                  <TableCell align="right">{reportNumber(row, 'AttendedCount', 'attendedCount')}</TableCell>
                  <TableCell align="right">{reportNumber(row, 'NoShowCount', 'noShowCount')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </>
    );
  };

  renderReceivables = () => {
    const { receivables } = this.state;
    const { classes } = this.props;
    if (!receivables) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const bookings = reportField(receivables, 'Bookings', 'bookings', []) || [];
    const abonements = reportField(receivables, 'Abonements', 'abonements', []) || [];
    const expiring = reportField(receivables, 'ExpiringAbonements', 'expiringAbonements', []) || [];
    const overdue = reportField(receivables, 'OverdueAbonements', 'overdueAbonements', []) || [];
    const truncated = reportField(receivables, 'Truncated', 'truncated', false);

    if (!bookings.length && !abonements.length && !expiring.length && !overdue.length) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    return (
      <>
        {truncated ? (
          <Typography variant="body2" color="textSecondary" className={classes.hint}>
            Показаны первые 200 строк в каждом списке.
          </Typography>
        ) : null}
        <Typography variant="body2" className={classes.hint}>
          <Link className={classes.inlineLink} to={this.sectionsHref('abonements')}>Абонементы</Link>
        </Typography>
        <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 0 }}>Открытые брони</Typography>
        {bookings.length === 0 ? (
          <Typography>Нет данных для отображения</Typography>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Площадка</TableCell>
                <TableCell align="right">Сумма</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((row) => (
                <TableRow key={`b-${reportField(row, 'Kind', 'kind', 'booking')}-${reportNumber(row, 'Id', 'id')}`}>
                  <TableCell>{moment(reportField(row, 'Date', 'date')).format('DD.MM.YYYY')}</TableCell>
                  <TableCell>{reportField(row, 'PlaygroundName', 'playgroundName', '-')}</TableCell>
                  <TableCell align="right">{this.formatCurrency(reportNumber(row, 'Price', 'price'))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="h6" className={classes.sectionTitle}>Неоплаченные абонементы</Typography>
        {this.renderAbonementStatusTable(abonements, true)}
        <Typography variant="h6" className={classes.sectionTitle}>Истекает</Typography>
        {this.renderAbonementStatusTable(expiring, false)}
        <Typography variant="h6" className={classes.sectionTitle}>Просрочен</Typography>
        {this.renderAbonementStatusTable(overdue, false)}
      </>
    );
  };

  renderAbonementStatusTable = (rows, showAmount) => {
    const { classes } = this.props;
    if (!rows || !rows.length) {
      return <Typography>Нет данных для отображения</Typography>;
    }
    return (
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>Клиент</TableCell>
            <TableCell>Период</TableCell>
            {showAmount ? <TableCell align="right">Сумма</TableCell> : <TableCell>Срок</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const expiry = abonementExpiryBadge(row);
            return (
              <TableRow key={reportNumber(row, 'Id', 'id')}>
                <TableCell>
                  <Link className={classes.inlineLink} to={this.sectionsHref('abonements')}>
                    {reportField(row, 'UserName', 'userName', '-')}
                  </Link>
                </TableCell>
                <TableCell>
                  {moment(reportField(row, 'DateFrom', 'dateFrom')).format('DD.MM.YYYY')}
                  {' — '}
                  {moment(reportField(row, 'DateTo', 'dateTo')).format('DD.MM.YYYY')}
                </TableCell>
                {showAmount ? (
                  <TableCell align="right">{this.formatCurrency(reportNumber(row, 'Amount', 'amount'))}</TableCell>
                ) : (
                  <TableCell style={{ color: ABONEMENT_EXPIRY_COLORS[expiry.tone] || ABONEMENT_EXPIRY_COLORS.muted }}>
                    {expiry.label}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  renderClients = () => {
    const { clients } = this.state;
    const { classes } = this.props;
    if (!clients) {
      return <Typography>Нет данных для отображения</Typography>;
    }
    const items = reportField(clients, 'Items', 'items', []) || [];
    const truncated = reportField(clients, 'Truncated', 'truncated', false);
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper className={classes.comparisonCard}>
              <Typography variant="body2" color="textSecondary">Клиенты организации</Typography>
              <Typography variant="h6" style={{ fontWeight: 600, marginTop: 8 }}>
                {reportNumber(clients, 'ClientCount', 'clientCount')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper className={classes.comparisonCard}>
              <Typography variant="body2" color="textSecondary">Созданы за период</Typography>
              <Typography variant="h6" style={{ fontWeight: 600, marginTop: 8 }}>
                {reportNumber(clients, 'CreatedInPeriod', 'createdInPeriod')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Typography variant="body2" className={classes.hint} style={{ marginTop: 16 }}>
          <Link className={classes.inlineLink} to={this.sectionsHref('clients')}>Клиенты</Link>
        </Typography>
        {truncated ? (
          <Typography variant="body2" color="textSecondary" className={classes.hint}>
            Показаны первые 200 строк в каждом списке.
          </Typography>
        ) : null}
        {!items.length ? (
          <Typography>Нет данных для отображения</Typography>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Клиент</TableCell>
                <TableCell>Телефон</TableCell>
                <TableCell>Источник</TableCell>
                <TableCell>Создан</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row, index) => (
                <TableRow key={clientRowKey(row, index)}>
                  <TableCell>{reportField(row, 'DisplayName', 'displayName', '-')}</TableCell>
                  <TableCell>{reportField(row, 'Phone', 'phone', '-')}</TableCell>
                  <TableCell>{reportField(row, 'Source', 'source', '-')}</TableCell>
                  <TableCell>{formatReportDate(reportField(row, 'CreatedAt', 'createdAt'))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </>
    );
  };

  renderPopularTimes = () => {
    const { popularTimes } = this.state;
    const { classes } = this.props;
    if (!popularTimes) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    const popularDays = (reportField(popularTimes, 'PopularDays', 'popularDays', []) || []).map(row => ({
      dayOfWeek: reportField(row, 'DayOfWeek', 'dayOfWeek', '-'),
      count: reportNumber(row, 'Count', 'count'),
      totalRevenue: reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      playgroundRevenue: reportNumber(row, 'PlaygroundRevenue', 'playgroundRevenue'),
      rentalItemsRevenue: reportNumber(row, 'RentalItemsRevenue', 'rentalItemsRevenue'),
      paidRevenue: reportNumber(row, 'PaidRevenue', 'paidRevenue'),
    }));

    const popularHours = (reportField(popularTimes, 'PopularHours', 'popularHours', []) || []).map(row => ({
      hour: reportNumber(row, 'Hour', 'hour'),
      count: reportNumber(row, 'Count', 'count'),
      totalRevenue: reportNumber(row, 'TotalRevenue', 'totalRevenue'),
      playgroundRevenue: reportNumber(row, 'PlaygroundRevenue', 'playgroundRevenue'),
      rentalItemsRevenue: reportNumber(row, 'RentalItemsRevenue', 'rentalItemsRevenue'),
      paidRevenue: reportNumber(row, 'PaidRevenue', 'paidRevenue'),
    }));

    if (popularDays.length === 0 && popularHours.length === 0) {
      return <Typography>Нет данных для отображения</Typography>;
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Популярные дни недели</Typography>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>День недели</TableCell>
                <TableCell align="right">Количество</TableCell>
                <TableCell align="right">Начислено</TableCell>
                <TableCell align="right">От аренды площадки</TableCell>
                <TableCell align="right">От доп услуг</TableCell>
                <TableCell align="right">Оплачено</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {popularDays.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.dayOfWeek}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.totalRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.playgroundRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.rentalItemsRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.paidRevenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Популярное время</Typography>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Час</TableCell>
                <TableCell align="right">Количество</TableCell>
                <TableCell align="right">Начислено</TableCell>
                <TableCell align="right">От аренды площадки</TableCell>
                <TableCell align="right">От доп услуг</TableCell>
                <TableCell align="right">Оплачено</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {popularHours.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.hour}:00</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.totalRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.playgroundRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.rentalItemsRevenue)}</TableCell>
                  <TableCell align="right">{this.formatCurrency(row.paidRevenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    );
  };

  renderPeriodComparison = () => {
    const { periodComparison, period1From, period1To, period2From, period2To } = this.state;
    const { classes } = this.props;

    if (!periodComparison) {
      return <Typography>Нет данных для сравнения</Typography>;
    }

    const Period1 = reportField(periodComparison, 'Period1', 'period1');
    const Period2 = reportField(periodComparison, 'Period2', 'period2');
    const Changes = reportField(periodComparison, 'Changes', 'changes');

    if (!Period1 || !Period2 || !Changes) {
      return <Typography>Неполные данные для сравнения</Typography>;
    }

    const revenueChange = reportNumber(Changes, 'RevenueChange', 'revenueChange');
    const countChange = reportNumber(Changes, 'CountChange', 'countChange');
    const revenueDifference = reportNumber(Changes, 'RevenueDifference', 'revenueDifference');
    const countDifference = reportNumber(Changes, 'CountDifference', 'countDifference');
    const revenueChangeColor = revenueChange >= 0 ? 'green' : 'red';
    const countChangeColor = countChange >= 0 ? 'green' : 'red';

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.comparisonCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
              Период 1: {moment(period1From).format('DD.MM.YYYY')} - {moment(period1To).format('DD.MM.YYYY')}
            </Typography>
            <Typography style={{ marginTop: 8, fontSize: '0.9375rem', color: '#495057' }}>
              Выручка: <strong style={{ color: '#212529' }}>{this.formatCurrency(reportNumber(Period1, 'Revenue', 'revenue'))}</strong>
            </Typography>
            <Typography style={{ fontSize: '0.9375rem', color: '#495057' }}>
              Количество: <strong style={{ color: '#212529' }}>{reportNumber(Period1, 'Count', 'count')}</strong>
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.comparisonCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
              Период 2: {moment(period2From).format('DD.MM.YYYY')} - {moment(period2To).format('DD.MM.YYYY')}
            </Typography>
            <Typography style={{ marginTop: 8, fontSize: '0.9375rem', color: '#495057' }}>
              Выручка: <strong style={{ color: '#212529' }}>{this.formatCurrency(reportNumber(Period2, 'Revenue', 'revenue'))}</strong>
            </Typography>
            <Typography style={{ fontSize: '0.9375rem', color: '#495057' }}>
              Количество: <strong style={{ color: '#212529' }}>{reportNumber(Period2, 'Count', 'count')}</strong>
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.comparisonCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>Изменения</Typography>
            <Typography style={{ color: revenueChangeColor }}>
              Изменение выручки: {revenueChange >= 0 ? '+' : ''}{revenueChange}%
              ({revenueDifference >= 0 ? '+' : ''}{this.formatCurrency(revenueDifference)})
            </Typography>
            <Typography style={{ color: countChangeColor }}>
              Изменение количества: {countChange >= 0 ? '+' : ''}{countChange}%
              ({countDifference >= 0 ? '+' : ''}{countDifference})
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  render() {
    const { classes, centers = [] } = this.props;
    const {
      activeTab,
      loading,
      errorMessage,
      fromDate,
      toDate,
      periodType,
      period1From,
      period1To,
      period2From,
      period2To,
    } = this.state;
    const centerId = this.getCenterId();
    const networkSelected = isNetworkCenter(centerId);
    const showNetworkOption = (centers || []).length > 1 || networkSelected;
    const selectValue = networkSelected || centers.some((c) => centerIdOf(c) === String(centerId))
      ? centerId
      : '';

    return (
      <AdminPageShell
        title="Отчёты"
        subtitle={networkSelected
          ? 'Деньги, загрузка, долги и нагрузка тренеров всех клубов сети'
          : 'Деньги, загрузка, долги и нагрузка тренеров выбранного клуба'}
      >
      <div className={classes.pageBody}>
        <Paper className={classes.paper}>
          <div className={classes.filterSection}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl className={classes.centerSelect} variant="outlined" size="small">
                  <InputLabel id="reports-center-select-label">Клуб (спорткомплекс)</InputLabel>
                  <Select
                    labelId="reports-center-select-label"
                    value={selectValue}
                    onChange={this.handleCenterSelectChange}
                    label="Клуб (спорткомплекс)"
                    displayEmpty
                  >
                    {showNetworkOption ? (
                      <MenuItem value={REPORT_NETWORK_CENTER_ID}>{REPORT_NETWORK_CENTER_LABEL}</MenuItem>
                    ) : null}
                    {centers.map((c) => {
                      const id = centerIdOf(c);
                      if (!id) return null;
                      return (
                        <MenuItem key={id} value={id}>
                          {centerNameOf(c)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              {activeTab === 6 ? (
                <>
                  <Grid item>
                    <Typography variant="subtitle2" className={classes.periodLabel}>Период 1:</Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="От"
                      type="date"
                      value={period1From}
                      onChange={(e) => this.handleFilterChange('period1From', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      className={classes.filterTextField}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="До"
                      type="date"
                      value={period1To}
                      onChange={(e) => this.handleFilterChange('period1To', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      className={classes.filterTextField}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" className={classes.periodLabel}>Период 2:</Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="От"
                      type="date"
                      value={period2From}
                      onChange={(e) => this.handleFilterChange('period2From', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      className={classes.filterTextField}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="До"
                      type="date"
                      value={period2To}
                      onChange={(e) => this.handleFilterChange('period2To', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      className={classes.filterTextField}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item>
                    <TextField
                      label="От"
                      type="date"
                      value={fromDate}
                      onChange={(e) => this.handleFilterChange('fromDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      className={classes.filterTextField}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="До"
                      type="date"
                      value={toDate}
                      onChange={(e) => this.handleFilterChange('toDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      className={classes.filterTextField}
                    />
                  </Grid>
                  {activeTab === 1 && (
                    <Grid item>
                      <TextField
                        select
                        label="Тип периода"
                        value={periodType}
                        onChange={(e) => this.handleFilterChange('periodType', e.target.value)}
                        SelectProps={{ native: true }}
                        variant="outlined"
                        className={classes.filterTextField}
                      >
                        <option value="day">День</option>
                        <option value="week">Неделя</option>
                        <option value="month">Месяц</option>
                        <option value="year">Год</option>
                      </TextField>
                    </Grid>
                  )}
                  {REPORT_PERIOD_PRESETS.map((preset) => (
                    <Grid item key={preset.key}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        className={classes.presetButton}
                        onClick={() => this.handlePeriodPreset(preset.key)}
                        disabled={!centerId}
                      >
                        {preset.label}
                      </Button>
                    </Grid>
                  ))}
                </>
              )}
              <Grid item>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={this.handleApplyFilters}
                  className={classes.applyButton}
                  disabled={!centerId}
                >
                  Применить
                </Button>
              </Grid>
              <Grid item>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className={classes.csvButton}
                  onClick={this.handleExportCsv}
                  disabled={!centerId || loading}
                >
                  Скачать CSV
                </Button>
              </Grid>
            </Grid>
          </div>
        </Paper>

        <Paper className={classes.paper}>
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            className={classes.tabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Сводка" />
            <Tab label="Деньги" />
            <Tab label="Загрузка" />
            <Tab label="Тренеры" />
            <Tab label="К получению" />
            <Tab label="Популярные дни/время" />
            <Tab label="Сравнение периодов" />
            <Tab label="Клиенты" />
          </Tabs>
        </Paper>

        <Paper className={classes.paper}>
          {errorMessage ? (
            <Typography color="error" className={classes.errorText}>{errorMessage}</Typography>
          ) : null}
          {!centerId ? (
            <Typography>Выберите клуб, чтобы увидеть отчёт.</Typography>
          ) : loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {activeTab === 0 && this.renderSummary()}
              {activeTab === 1 && this.renderMoney()}
              {activeTab === 2 && this.renderPlaygroundsStatistics()}
              {activeTab === 3 && this.renderTrainers()}
              {activeTab === 4 && this.renderReceivables()}
              {activeTab === 5 && this.renderPopularTimes()}
              {activeTab === 6 && this.renderPeriodComparison()}
              {activeTab === 7 && this.renderClients()}
            </>
          )}
        </Paper>
      </div>
      </AdminPageShell>
    );
  }
}

export default connect(
  (state) => ({
    centers: state.centers || [],
  }),
  { fetchCenters }
)(withStyles(styles)(Reports));
